import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const QUICK_PROMPTS = [
  "What programs do you offer?",
  "How much does a consultation cost?",
  "Do you help with PCOS?",
  "Can couples join together?",
];

const linkify = (text) => {
  // Process markdown: bold, links, headers, lists
  const lines = text.split("\n");
  const elements = [];

  lines.forEach((line, lineIdx) => {
    // Skip empty lines but keep spacing
    if (!line.trim()) {
      elements.push(<br key={`br-${lineIdx}`} />);
      return;
    }

    // Headers (## or ###)
    if (line.startsWith("### ")) {
      elements.push(<strong key={lineIdx} className="block text-emerald_deep text-xs uppercase tracking-wider mt-2 mb-1">{processInline(line.slice(4))}</strong>);
      return;
    }
    if (line.startsWith("## ")) {
      elements.push(<strong key={lineIdx} className="block text-emerald_deep mt-2 mb-1">{processInline(line.slice(3))}</strong>);
      return;
    }

    // List items
    if (line.startsWith("- ") || line.startsWith("• ")) {
      elements.push(
        <div key={lineIdx} className="flex items-start gap-1.5 ml-1">
          <span className="text-accent mt-1.5 text-[8px]">●</span>
          <span>{processInline(line.slice(2))}</span>
        </div>
      );
      return;
    }

    // Table rows (skip separator rows like |---|---|)
    if (line.startsWith("|") && !line.match(/^\|[\s-|]+\|$/)) {
      const cells = line.split("|").filter(c => c.trim());
      elements.push(
        <div key={lineIdx} className="flex gap-2 text-xs py-0.5">
          {cells.map((cell, ci) => (
            <span key={`cell-${ci}-${cell.trim().slice(0,20)}`} className={ci === 0 ? "flex-1" : "text-right min-w-[80px]"}>
              {processInline(cell.trim())}
            </span>
          ))}
        </div>
      );
      return;
    }
    if (line.match(/^\|[\s-|]+\|$/)) return; // skip table separators

    elements.push(<span key={lineIdx} className="block">{processInline(line)}</span>);
  });

  return elements;
};

const processInline = (text) => {
  // Process bold, links
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const partKey = `${i}-${part.slice(0,15)}`;
    // Bold
    const bold = part.match(/^\*\*(.+)\*\*$/);
    if (bold) return <strong key={partKey} className="font-semibold">{bold[1]}</strong>;
    // Links
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const isExternal = link[2].startsWith("http");
      return (
        <a key={partKey} href={link[2]}
          target={isExternal ? "_blank" : "_self"}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className="text-accent underline underline-offset-2 hover:text-accent_light transition-colors">
          {link[1]}
        </a>
      );
    }
    return <span key={partKey}>{part}</span>;
  });
};

const ChatMessage = ({ msg }) => {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`} data-testid={`chat-msg-${msg.role}`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
          <span className="text-accent font-serif text-xs font-semibold">F</span>
        </div>
      )}
      <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser
          ? "bg-emerald_deep text-bone rounded-2xl rounded-br-md"
          : "bg-surface border border-border_custom text-text_main rounded-2xl rounded-bl-md"
      }`}>
        {isUser ? msg.content : linkify(msg.content)}
        {msg.typing && <span className="inline-block w-1.5 h-4 bg-accent/60 ml-0.5 animate-pulse rounded-sm" />}
      </div>
    </div>
  );
};

const TypingIndicator = () => (
  <div className="flex justify-start mb-3" data-testid="chat-typing">
    <div className="w-7 h-7 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mr-2 mt-0.5">
      <span className="text-accent font-serif text-xs font-semibold">F</span>
    </div>
    <div className="bg-surface border border-border_custom px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-1.5">
      <span className="w-2 h-2 bg-text_muted/40 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-2 h-2 bg-text_muted/40 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-2 h-2 bg-text_muted/40 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
    </div>
  </div>
);

const AIChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [visible, setVisible] = useState(false);
  const [typingIdx, setTypingIdx] = useState(-1);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [unread, setUnread] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const typingCleanupRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only, setVisible is stable

  // Proactive greeting after 30 seconds
  useEffect(() => {
    if (hasGreeted) return;
    const greeted = sessionStorage.getItem("chat_greeted");
    if (greeted) { setHasGreeted(true); return; }
    const timer = setTimeout(() => {
      if (!open) {
        setUnread(true);
      }
      setHasGreeted(true);
      sessionStorage.setItem("chat_greeted", "1");
    }, 30000);
    return () => clearTimeout(timer);
  }, [hasGreeted, open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current && !loading) {
      inputRef.current.focus();
    }
  }, [open, loading]);

  // Clean up typing interval on unmount
  useEffect(() => {
    return () => {
      if (typingCleanupRef.current) typingCleanupRef.current();
    };
  }, []);

  // Word-by-word typing effect
  const typeResponse = useCallback((fullText, msgIndex) => {
    if (typingCleanupRef.current) typingCleanupRef.current();
    const words = fullText.split(/(\s+)/);
    let displayed = "";
    let i = 0;
    const interval = setInterval(() => {
      if (i < words.length) {
        displayed += words[i];
        i++;
        setMessages(prev => {
          const updated = [...prev];
          if (updated[msgIndex]) {
            updated[msgIndex] = { ...updated[msgIndex], content: displayed, typing: true };
          }
          return updated;
        });
      } else {
        clearInterval(interval);
        typingCleanupRef.current = null;
        setMessages(prev => {
          const updated = [...prev];
          if (updated[msgIndex]) {
            updated[msgIndex] = { ...updated[msgIndex], content: fullText, typing: false };
          }
          return updated;
        });
        setTypingIdx(-1);
      }
    }, 20);
    typingCleanupRef.current = () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- interval refs captured intentionally

  const sendMessage = useCallback(async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: "user", content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text.trim(), session_id: sessionId }),
      });

      const data = await res.json();
      if (data.session_id) setSessionId(data.session_id);

      const newIdx = messages.length + 1; // +1 for user msg we just added
      setMessages(prev => [...prev, { role: "assistant", content: "", typing: true, id: `asst-${Date.now()}` }]);
      setLoading(false);
      setTypingIdx(newIdx);
      typeResponse(data.response, newIdx);

    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error("Chat error:", err);
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: "I'm sorry, I'm having trouble connecting. Please try again or reach us on WhatsApp at +91 91088 18369.", typing: false, id: `err-${Date.now()}` }
      ]);
      setLoading(false);
    }
  }, [loading, sessionId, messages.length, typeResponse]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  if (!visible) return null;

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-32px)] h-[520px] max-h-[calc(100vh-120px)] bg-bone rounded-2xl shadow-2xl border border-emerald_deep/10 flex flex-col overflow-hidden"
          data-testid="ai-chat-window">
          {/* Header */}
          <div className="bg-emerald_deep px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-accent/20 flex items-center justify-center">
                <span className="text-accent font-serif text-sm font-semibold">F</span>
              </div>
              <div>
                <p className="text-bone text-sm font-semibold">Fitly India</p>
                <p className="text-bone/50 text-xs flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                  AI Wellness Assistant
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-chat-close"
              className="text-bone/50 hover:text-bone transition-colors">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-1" data-testid="ai-chat-messages">
            {messages.length === 0 && !loading && (
              <div className="text-center py-6">
                <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
                  <span className="text-accent font-serif text-xl font-semibold">F</span>
                </div>
                <p className="text-sm text-emerald_deep font-medium mb-1">Welcome to Fitly India</p>
                <p className="text-xs text-text_muted mb-6 leading-relaxed max-w-[240px] mx-auto">
                  Ask me about our fertility programs, nutrition, pricing, or any wellness question.
                </p>
                <div className="space-y-2">
                  {QUICK_PROMPTS.map((q) => (
                    <button key={q} onClick={() => sendMessage(q)}
                      data-testid={`quick-prompt-${q.slice(0, 15).replace(/\s/g, "-").toLowerCase()}`}
                      className="block w-full text-left px-4 py-2.5 rounded-xl text-xs text-emerald_deep bg-surface border border-border_custom hover:bg-emerald_deep/5 hover:border-accent/30 transition-all">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg) => (
              <ChatMessage key={msg.id || `msg-${msg.content?.slice(0,20)}`} msg={msg} />
            ))}
            {loading && <TypingIndicator />}
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit}
            className="flex items-center gap-2 px-4 py-3 border-t border-border_custom bg-surface flex-shrink-0">
            <input ref={inputRef} type="text" value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about fertility, programs, pricing..."
              disabled={loading || typingIdx >= 0}
              data-testid="ai-chat-input"
              className="flex-1 bg-bone border border-border_custom rounded-xl px-4 py-2.5 text-sm text-text_main placeholder:text-text_muted/50 focus:outline-none focus:border-accent/50 transition-colors disabled:opacity-50" />
            <button type="submit" disabled={!input.trim() || loading || typingIdx >= 0}
              data-testid="ai-chat-send"
              className="w-10 h-10 rounded-xl bg-emerald_deep text-bone flex items-center justify-center hover:bg-emerald_deep/90 transition-colors disabled:opacity-40 flex-shrink-0">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          </form>

          <div className="px-4 py-1.5 bg-surface border-t border-border_custom flex-shrink-0">
            <p className="text-[10px] text-text_muted/50 text-center">AI assistant — not medical advice. Consult your doctor for personalised guidance.</p>
          </div>
        </div>
      )}

      {/* Greeting bubble */}
      {unread && !open && (
        <div className="fixed bottom-[84px] right-24 z-50 max-w-[220px] animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="chat-greeting">
          <div className="bg-emerald_deep text-bone px-4 py-3 rounded-2xl rounded-br-sm shadow-lg text-sm leading-relaxed">
            Hi! Have questions about fertility nutrition? I&apos;m here to help.
          </div>
          <button onClick={() => setUnread(false)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-bone border border-border_custom rounded-full flex items-center justify-center text-text_muted hover:text-emerald_deep shadow-sm">
            <X size={10} />
          </button>
        </div>
      )}

      {/* FAB */}
      <button onClick={() => { setOpen(!open); setUnread(false); }} data-testid="ai-chat-fab"
        className={`fixed bottom-6 right-24 z-50 w-14 h-14 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center ${
          open ? "bg-emerald_deep/80 text-bone" : "bg-emerald_deep text-bone"
        }`}
        aria-label={open ? "Close chat" : "Open AI chat"}>
        {open ? <X size={22} /> : <MessageSquare size={22} />}
        {unread && !open && (
          <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-accent rounded-full border-2 border-bone animate-pulse" />
        )}
      </button>
    </>
  );
};

export default AIChatWidget;
